import getIdentifier from 'getIdentifier';
import { toFilterPlate } from 'migrateScope';
import { AnswerSourceFilter } from 'services/streamInsightsQuery';

// Inverse of `toClause` in buildReportInsightsView.ts: turns a filter the agent
// applied (streamed back as an AnswerSourceFilter, shape {field, operator, values})
// into a report FixedFilter, so it can be carried through to the report's
// drill-downs.
//
// The `kind` discriminator is stripped on the wire (toSourceFilters, server), and
// we need the exact numeric subtype (int|long|float) anyway — so the field's type
// comes from the report's own field metadata (fieldDef), not the wire. This also
// means it works for filters restored from a persisted conversation, which never
// carried a kind.
//
// Returns undefined when there's no drill-down equivalent (date fields, an unmapped
// operator, empty or non-numeric values) — those are skipped by the caller.

// Inverse of the number-operator mapping in toClause (buildReportInsightsView.ts).
const NUMBER_OPERATOR: {
  [op: string]: FilterRangeOperator | undefined;
} = {
  gte: 'is more than or equal to',
  gt: 'is more than',
  lt: 'is less than',
  lte: 'is less than or equal to',
  between: 'is between',
};

const answerSourceFilterToFixedFilter = (
  source: AnswerSourceFilter,
  fieldDef: FleetOps.Field,
): FixedFilter | undefined => {
  const { field, operator, values } = source;
  if (values.length === 0) {
    return undefined;
  }

  const base = {
    id: getIdentifier(),
    field,
    dataset: fieldDef.dataType,
    mode: 'plate' as const,
  };

  if (fieldDef.type === 'text') {
    const textFilter: TextFilter = {
      ...base,
      fieldType: 'text',
      keywordValues: values.map(String),
      isExclude: operator === 'is not',
    };
    return textFilter;
  }

  if (fieldDef.type === 'boolean') {
    const booleanFilter: BooleanFilter = {
      ...base,
      fieldType: 'boolean',
      booleanValue: Boolean(values[0]),
      isExclude: operator === 'is not',
    };
    return booleanFilter;
  }

  if (
    fieldDef.type === 'int' ||
    fieldDef.type === 'long' ||
    fieldDef.type === 'float'
  ) {
    const rangeOperator = NUMBER_OPERATOR[operator];
    if (!rangeOperator) {
      return undefined;
    }
    const valueA = Number(values[0]);
    const valueB =
      rangeOperator === 'is between' ? Number(values[1]) : undefined;
    if (
      Number.isNaN(valueA) ||
      (rangeOperator === 'is between' && Number.isNaN(valueB))
    ) {
      return undefined;
    }
    const numberFilter: NumberFilter = {
      ...base,
      fieldType: fieldDef.type,
      rangeValue: {
        operator: rangeOperator,
        valueA,
        ...(valueB !== undefined ? { valueB } : {}),
      },
    };
    return numberFilter;
  }

  // date / dateText / geo_point have no drill-down equivalent here.
  return undefined;
};

// Convenience wrapper: build the FixedFilter and wrap it as a Fixed plate ready to
// push onto FilterPlatesContext.drillDowns.
export const answerSourceFilterToPlate = (
  source: AnswerSourceFilter,
  fieldDef: FleetOps.Field,
): FilterPlateType | undefined => {
  const fixed = answerSourceFilterToFixedFilter(source, fieldDef);
  return fixed ? toFilterPlate(fixed) : undefined;
};

export default answerSourceFilterToFixedFilter;
